import { useId, useState } from 'react';
import { Shuffle } from 'lucide-react';
import { useLocale } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface ReplacementBadgeProps {
  /** The slot to render the replacement badge for; only the replacement-related fields are read. */
  slot?: {
    isReplacement?: boolean;
    originalCompletedCount?: number;
    replacementAfterMatch?: number | null;
    replacedPlayerId?: string;
    playerId?: string;
    replacementTimestamp?: number | string | null;
    replacementPhase?: string | number;
    replacementRound?: number | string;
    replacementTotalMatches?: number;
    firstMatchAsReplacement?: {
      matchNumber?: number | string;
      phase?: string | number;
      round?: number | string;
      court?: number | string;
      time?: string;
      team1Names?: string;
      team2Names?: string;
      date?: string;
    } | null;
  } | null;
  /** Player list used to resolve the replaced player and the replacement by id.
   *  Permissive shape so both the domain `Player` and lighter `{id,name,elo}` lists pass. */
  players?: Array<{ id?: string; name?: string; elo?: number | string }>;
  /** Use the lighter accent colours suited to a dark background. */
  darkBg?: boolean;
}

/**
 * Innbytter badge — a small clickable marker that opens a modal explaining a mid-tournament player replacement.
 * Extracted verbatim from the original monolith (legacy/App.jsx); markup unchanged so styling is identical.
 */
function ReplacementBadge({ slot, players, darkBg = false }: ReplacementBadgeProps) {
  const [showInfo, setShowInfo] = useState(false);
  const { t, formatDate } = useLocale();
  const uid = safeId('replacement-info', useId());
  if (!slot?.isReplacement) return null;

  // Hide the badge if the original player had not played a single match when the swap happened.
  // (i.e. a setup correction, not an actual mid-tournament substitution)
  // Uses originalCompletedCount when stored (newer swaps), falling back to
  // replacementAfterMatch for old swaps from before this logic was added.
  const originalPlayedCount = slot.originalCompletedCount ?? slot.replacementAfterMatch ?? 0;
  if (originalPlayedCount === 0) return null;

  const replaced = players?.find((p) => p.id === slot.replacedPlayerId);
  const replacement = players?.find((p) => p.id === slot.playerId);
  const timestamp = slot.replacementTimestamp;

  // Format timestamp
  const formatTime = (ts) => {
    if (!ts) return t('players.replacementBadge.unknownTime');
    const d = new Date(ts);
    return formatDate(d, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowInfo(true);
        }}
        className="inline-flex items-center justify-center font-bold flex-shrink-0 transition-all hover:brightness-110 active:scale-95 cursor-pointer"
        style={{
          background: darkBg ? '#fbbf24' : '#f59e0b',
          color: '#0f1611',
          width: 18,
          height: 18,
          borderRadius: 4,
          border: `1.5px solid ${darkBg ? '#fef3c7' : '#d97706'}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
        title={t('players.replacementBadge.buttonTitle')}
      >
        <Shuffle size={11} strokeWidth={3} />
      </button>

      {showInfo && (
        // A `Dialog` (#900), named by its kicker and title. Escape and the
        // backdrop close it, and focus goes back to the badge.
        //
        // The badge sits inside clickable rows (a match, a table line). The
        // panel stopped its clicks, but a click on the BACKDROP closed the
        // popup and then bubbled on into the row behind it; a `Dialog` ends
        // every click at its own edge, so dismissing it is now only that.
        <Dialog
          labelledBy={`${uid}-kicker ${uid}-title`}
          onClose={() => setShowInfo(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15, 22, 17, 0.6)',
            backdropFilter: 'blur(4px)',
          }}
          className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
          style={{
            maxHeight: 'calc(100dvh - 2rem)',
            background: '#ffffff',
            maxWidth: 420,
            width: '100%',
            borderRadius: 16,
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
            border: '1px solid #e7eae3',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid #e7eae3',
              background: '#fef3c7',
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: '#f59e0b',
                  color: '#0f1611',
                  border: '1.5px solid #d97706',
                }}
              >
                <Shuffle size={14} strokeWidth={3} />
              </div>
              <div>
                <div
                  id={`${uid}-kicker`}
                  className="font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: '#92400e' }}
                >
                  {t('players.replacementBadge.kicker')}
                </div>
                <h3
                  id={`${uid}-title`}
                  className="font-display text-base font-semibold"
                  style={{ color: '#0f1611' }}
                >
                  {t('players.replacementBadge.title')}
                </h3>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '16px 18px' }}>
            {replaced && replacement ? (
              <>
                {/* Original player (swapped out) */}
                <div className="mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">
                    {t('players.replacementBadge.swappedOut')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: '#fee2e2', color: '#b91c1c' }}
                    >
                      ⊘
                    </span>
                    <span className="text-sm font-medium text-ink line-through opacity-70">
                      {replaced.name}
                    </span>
                    <span className="text-xs font-mono text-ink-3">ELO {replaced.elo}</span>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center gap-2 my-2 ml-1">
                  <span className="text-ink-3 text-lg">↓</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                    {t('players.replacementBadge.replacedBy')}
                  </span>
                </div>

                {/* Replacement */}
                <div className="mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">
                    {t('players.replacementBadge.kicker')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        background: '#f59e0b',
                        color: '#0f1611',
                        border: '1.5px solid #d97706',
                      }}
                    >
                      <Shuffle size={10} strokeWidth={3} />
                    </span>
                    <span className="text-sm font-medium text-ink">{replacement.name}</span>
                    <span className="text-xs font-mono text-ink-3">ELO {replacement.elo}</span>
                  </div>
                </div>

                {/* Tournament context */}
                <div
                  className="mt-4 pt-3 grid grid-cols-2 gap-3"
                  style={{ borderTop: '1px solid #e7eae3' }}
                >
                  {/* Timestamp */}
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-0.5">
                      {t('players.replacementBadge.time')}
                    </div>
                    <div className="text-xs text-ink-2">{formatTime(timestamp)}</div>
                  </div>

                  {/* Phase/round */}
                  {slot.replacementPhase && (
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-0.5">
                        {t('players.replacementBadge.phase')}
                      </div>
                      <div className="text-xs text-ink-2">
                        {slot.replacementPhase === 'finals'
                          ? t('players.replacementBadge.finals')
                          : t('players.replacementBadge.phaseN', {
                              phase: String(slot.replacementPhase),
                            })}
                        {slot.replacementRound
                          ? t('players.replacementBadge.roundSuffix', {
                              round: String(slot.replacementRound),
                            })
                          : ''}
                      </div>
                    </div>
                  )}

                  {/* Matches played */}
                  {slot.replacementAfterMatch != null && (
                    <div className="col-span-2">
                      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-0.5">
                        {t('players.replacementBadge.matchesPlayedLabel')}
                      </div>
                      <div className="text-xs text-ink-2">
                        {t('players.replacementBadge.matchesProgress', {
                          done: String(slot.replacementAfterMatch),
                          total: String(slot.replacementTotalMatches || '?'),
                        })}
                      </div>
                      <div
                        className="mt-1 h-1.5 rounded-full overflow-hidden"
                        style={{ background: '#e7eae3' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: slot.replacementTotalMatches
                              ? `${Math.round((slot.replacementAfterMatch / slot.replacementTotalMatches) * 100)}%`
                              : '0%',
                            background: '#f59e0b',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* First match as replacement */}
                {slot.firstMatchAsReplacement && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e7eae3' }}>
                    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-2">
                      {t('players.replacementBadge.firstMatchFor', {
                        name:
                          replacement?.name?.split(' ')[0] ||
                          t('players.replacementBadge.theSubstitute'),
                      })}
                    </div>
                    <div
                      className="p-3 rounded-lg"
                      style={{ background: '#fefce8', border: '1px solid #fde68a' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                          {slot.firstMatchAsReplacement.matchNumber
                            ? t('players.replacementBadge.matchPrefix', {
                                number: String(slot.firstMatchAsReplacement.matchNumber),
                              })
                            : ''}
                          {slot.firstMatchAsReplacement.phase === 'finals'
                            ? t('players.replacementBadge.final')
                            : t('players.replacementBadge.phaseN', {
                                phase: String(slot.firstMatchAsReplacement.phase),
                              })}
                          {slot.firstMatchAsReplacement.round
                            ? t('players.replacementBadge.roundShort', {
                                round: String(slot.firstMatchAsReplacement.round),
                              })
                            : ''}
                        </span>
                        <span className="font-mono text-[10px] text-ink-3">
                          {t('players.replacementBadge.court', {
                            court: String(slot.firstMatchAsReplacement.court || '?'),
                          })}
                          {slot.firstMatchAsReplacement.time
                            ? ` · ${slot.firstMatchAsReplacement.time}`
                            : ''}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-ink">
                          {slot.firstMatchAsReplacement.team1Names}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-2">
                          vs
                        </span>
                        <span className="text-sm font-medium text-ink text-right">
                          {slot.firstMatchAsReplacement.team2Names}
                        </span>
                      </div>
                      {slot.firstMatchAsReplacement.date && (
                        <div className="font-mono text-[10px] text-ink-3 mt-1.5">
                          {slot.firstMatchAsReplacement.date}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-ink-3 italic">
                {t('players.replacementBadge.missingInfo')}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '10px 18px',
              borderTop: '1px solid #e7eae3',
              background: '#fafbf7',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button type="button" onClick={() => setShowInfo(false)} className="btn-ghost text-xs">
              {t('players.replacementBadge.close')}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

export { ReplacementBadge };
